import React, { useState, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { format } from 'date-fns'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Button } from '~/ui/button'
import { Input } from '~/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  Clock,
  Music,
  TrendingUp,
  Activity,
} from 'lucide-react'

const PLAYBACK_ANALYTICS_QUERY = gql(`
  query PlaybackAnalytics(
    $organizationId: ID
    $zoneId: ID
    $startDate: ISO8601Date
    $endDate: ISO8601Date
    $days: Int
  ) {
    playbackAnalytics(
      organizationId: $organizationId
      zoneId: $zoneId
      startDate: $startDate
      endDate: $endDate
      days: $days
    ) {
      totalListeningTimeSeconds
      totalListeningTimeHours
      totalTracksPlayed
      uniqueTracksPlayed
      averageTrackCompletionPercentage
      mostActiveHours
      dailyListeningHours {
        date
        hours
        tracksPlayed
      }
      topTracks {
        trackId
        trackTitle
        trackArtist
        playCount
        totalListeningTimeSeconds
        averageCompletionPercentage
        track {
          id
          name
          displayArtist
        }
      }
      dateRange {
        startDate
        endDate
        days
      }
    }
  }
`)

interface PlaybackAnalyticsDashboardProps {
  organizationId?: string
  zoneId?: string
  zoneName?: string
}

const CHART_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
  '#84CC16',
  '#F97316',
]

export const PlaybackAnalyticsDashboard: React.FC<PlaybackAnalyticsDashboardProps> = ({
  organizationId,
  zoneId,
  zoneName,
}) => {
  const [dateRange, setDateRange] = useState({
    preset: '7',
    startDate: '',
    endDate: '',
  })

  const queryVariables = useMemo(() => {
    let variables: any = {
      organizationId,
      zoneId,
    }

    if (dateRange.preset !== 'custom') {
      variables.days = parseInt(dateRange.preset)
    } else if (dateRange.startDate && dateRange.endDate) {
      variables.startDate = dateRange.startDate
      variables.endDate = dateRange.endDate
    }

    return variables
  }, [organizationId, zoneId, dateRange])

  const { data, loading, error, refetch } = useQuery(PLAYBACK_ANALYTICS_QUERY, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
  })

  const analytics = data?.playbackAnalytics

  const formatHours = (hours: number) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`
    }
    return `${hours.toFixed(1)}h`
  }

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const chartData = useMemo(() => {
    if (!analytics?.dailyListeningHours) return []

    return analytics.dailyListeningHours.map((day) => ({
      date: format(new Date(day.date), 'MMM d'),
      hours: day.hours,
      tracks: day.tracksPlayed,
    }))
  }, [analytics?.dailyListeningHours])

  const hourlyData = useMemo(() => {
    if (!analytics?.mostActiveHours) return []

    const hourCounts = new Array(24).fill(0)
    analytics.mostActiveHours.forEach((hour) => {
      hourCounts[hour] = 1 // We'd need more data to show actual counts
    })

    return hourCounts.map((_, hour) => ({
      hour: hour,
      label:
        hour === 0 ? '12am' : hour < 12 ? `${hour}am` : hour === 12 ? '12pm' : `${hour - 12}pm`,
      active: analytics.mostActiveHours.includes(hour),
    }))
  }, [analytics?.mostActiveHours])

  const topTracksData = useMemo(() => {
    if (!analytics?.topTracks) return []

    return analytics.topTracks.slice(0, 10).map((track, index) => ({
      ...track,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }))
  }, [analytics?.topTracks])

  if (loading && !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return <div className="p-4 text-red-600">Error loading analytics: {error.message}</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Playback Analytics</h2>
          {zoneName && <p className="text-gray-600">Zone: {zoneName}</p>}
        </div>

        <div className="flex gap-4">
          <Select
            value={dateRange.preset}
            onValueChange={(value) => setDateRange((prev) => ({ ...prev, preset: value }))}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem>
              <SelectItem value="7">7 Days</SelectItem>
              <SelectItem value="30">30 Days</SelectItem>
              <SelectItem value="90">90 Days</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>

          {dateRange.preset === 'custom' && (
            <>
              <Input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                className="w-40"
              />
              <Input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                className="w-40"
              />
            </>
          )}

          <Button onClick={() => refetch()} variant="outline" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Listening Time</CardTitle>
            <Clock className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatTime(analytics?.totalListeningTimeSeconds || 0)}
            </div>
            <p className="text-muted-foreground text-xs">
              {formatHours(analytics?.totalListeningTimeHours || 0)} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tracks Played</CardTitle>
            <Music className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalTracksPlayed || 0}</div>
            <p className="text-muted-foreground text-xs">
              {analytics?.uniqueTracksPlayed || 0} unique tracks
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Completion</CardTitle>
            <TrendingUp className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.averageTrackCompletionPercentage
                ? `${analytics.averageTrackCompletionPercentage.toFixed(1)}%`
                : 'N/A'}
            </div>
            <p className="text-muted-foreground text-xs">Track completion rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Most Active Hours</CardTitle>
            <Activity className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.mostActiveHours?.length || 0}</div>
            <p className="text-muted-foreground text-xs">Peak activity periods</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Daily Listening Hours */}
        <Card>
          <CardHeader>
            <CardTitle>Daily Listening Hours</CardTitle>
            <CardDescription>
              Listening activity over the past {analytics?.dateRange?.days || 7} days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip
                  formatter={(value, name) => [
                    name === 'hours' ? formatHours(value as number) : value,
                    name === 'hours' ? 'Hours' : 'Tracks',
                  ]}
                />
                <Bar dataKey="hours" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Most Active Hours */}
        <Card>
          <CardHeader>
            <CardTitle>Most Active Hours</CardTitle>
            <CardDescription>Hours of the day with highest activity</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-8 gap-2">
              {hourlyData.map((hourData) => (
                <div key={hourData.hour} className="text-center">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium ${
                      hourData.active ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {hourData.hour}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">{hourData.label}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Tracks */}
      <Card>
        <CardHeader>
          <CardTitle>Top Tracks</CardTitle>
          <CardDescription>Most played tracks in the selected period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {topTracksData.map((track, index) => (
              <div
                key={track.trackId || index}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: track.color }}
                    />
                  </div>
                  <div>
                    <div className="font-medium">{track.trackTitle}</div>
                    <div className="text-sm text-gray-500">{track.trackArtist}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium">{track.playCount} plays</div>
                  {track.totalListeningTimeSeconds > 0 && (
                    <div className="text-sm text-gray-500">
                      {formatTime(track.totalListeningTimeSeconds)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
